// SEC2-S3 (docs/build-briefs/SEC2-device-setup-code-binding.md section 4.1, and the S1 review's carry-forward
// in 16.1): the tablet generates its OWN device credential, 32 random bytes from the platform's secure source
// (crypto.getRandomValues), never Math.random and never a value the server chose. It is sent once with the
// setup code, and afterwards as the X-Device-Token header on sign-in. Generating it on the tablet is what makes
// a redemption safe to retry after a lost response: the retry presents the same secret and gets the same device.
//
// The server keeps only a hash. The credential is kept in localStorage beside the terminal config
// (ui/src/session.ts), which is why that file's old "carries no credential" comment was rewritten.
// Never log it, never put it in a URL, never show it.

export const DEVICE_TOKEN_HEADER = "X-Device-Token";

/** What generateDeviceSecret needs from crypto. Injectable so a test can see exactly what was asked of it. */
export interface RandomSource {
  getRandomValues(array: Uint8Array<ArrayBuffer>): unknown;
}

const SECRET_BYTES = 32;
// The server refuses a secret with fewer distinct characters than this among the 43
// (src/domain/deviceBinding.ts MIN_DEVICE_SECRET_DISTINCT_CHARACTERS). Checking here too means a broken
// random source fails loudly on the tablet, before the setup code is spent.
const MIN_DISTINCT_CHARACTERS = 16;

function toBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** 32 random bytes as 43 base64url characters. */
export function generateDeviceSecret(source: RandomSource = globalThis.crypto): string {
  const bytes = new Uint8Array(SECRET_BYTES);
  source.getRandomValues(bytes);
  const secret = toBase64Url(bytes);
  if (new Set(secret).size < MIN_DISTINCT_CHARACTERS) {
    throw new Error("The random source did not give a usable device secret.");
  }
  return secret;
}
